import type { Asset } from '@schibsted-svp/svp-api-types';
import type { ResolvedPermissionValue } from '@vgtv/consent-manager';
import type { Player } from '@schibsted-svp/web-player';
import type { AdnSiteConfig, DeviceType } from '../types';
import {
  prefixExternalKeywords,
  prepareXandrKeywords,
} from '../prepareKeywords';
import {
  getAssetAspectRatio,
  getAssetEmbeddedTags,
  getAssetMetadata,
} from '../../../utils/player/asset';
import { formatTagValue } from './formatTagValue';
import { getEnvironmentKeywords } from './getEnvironmentKeywords';
import { getQueryStringKeywords } from './getQueryStringKeywords';
import { buildConsentKeywords } from '../buildConsentKeywords';
import { getClientKeywords } from '../getClientKeywords';
import { getPlayerAspectRatio } from '../getPlayerAspectRatio';

export function getAssetKeywords(
  asset: Asset,
  dynamicAR = false,
  playerAspectRatio?: number
) {
  const categoryTitle = asset.category.title;
  const { contentType } = getAssetMetadata(asset);
  const keywords: Record<
    string,
    string | (string | undefined)[] | boolean | undefined
  > = {
    'article-id': String(asset.id),
    'kw_se-generic-articleid': String(asset.id),
    'kw_aa-sch-content_type': [
      `${formatTagValue(categoryTitle)}_${contentType}`,
      formatTagValue(contentType),
    ].filter(Boolean),
    'kw_se-generic-tags': getAssetEmbeddedTags(asset)
      .map(({ tag }) => tag)
      .map(formatTagValue),
  };

  return {
    ...keywords,
    ...getClientKeywords(
      dynamicAR,
      getAssetAspectRatio(asset),
      playerAspectRatio
    ),
  };
}

type GlobalKeywordsInput = Partial<
  Record<
    | 'countryCode'
    | 'supplyType'
    | 'publisherName'
    | 'pageType'
    | 'publisherGroup'
    | 'newsSection',
    string | string[]
  >
>;

type GlobalKeywordsOptions = {
  // Whether to append the tcf/gdpr consent keywords. Defaults to `true`;
  // the VMAP path turns it off.
  includeTcfConsent?: boolean;
};

// Builds the request-level keyword set shared by the classic ADN and VMAP
// paths. Both emit `kw_`-prefixed Xandr keywords only; the only difference
// is that the tcf/gdpr consent keywords are appended unless
// `includeTcfConsent` is `false` (VMAP).
export const getGlobalKeywords = async (
  keywords: GlobalKeywordsInput,
  asset: Asset,
  deviceType: DeviceType,
  cmpConsent: ResolvedPermissionValue,
  dynamicAR: boolean,
  player: Player,
  siteConfig?: AdnSiteConfig<'ab' | 'svd'>,
  { includeTcfConsent = true }: GlobalKeywordsOptions = {}
) => {
  const newsroom =
    player.getRawConfig().vendor === 'svd' ? 'svd' : 'aftonbladet';

  return prepareXandrKeywords(
    Object.assign(
      {
        'kw_aa-sch-country_code': keywords.countryCode,
        'kw_aa-sch-supply_type': keywords.supplyType,
        'kw_aa-sch-publisher': keywords.publisherName,
        'kw_aa-sch-page_type': keywords.pageType,
        'kw_se-generic-publishergroup': keywords.publisherGroup,
        'kw_se-generic-page': keywords.pageType,
        'kw_se-generic-section': keywords.newsSection,
        [`kw_se-${newsroom}-section`]: keywords.newsSection,
      },
      getEnvironmentKeywords(player!, deviceType),
      prefixExternalKeywords(getQueryStringKeywords()),
      getAssetKeywords(asset, dynamicAR, getPlayerAspectRatio(player)),
      await buildConsentKeywords({
        player,
        glimrConfig: siteConfig?.glimr,
        cmpConsent,
        includeTcfConsent,
      })
    )
  );
};
